import { Component, ChangeDetectionStrategy } from "@angular/core";
import { Router } from "@angular/router";
import { DragulaService } from "ng2-dragula";
import { from, Subject } from "rxjs";
import { debounceTime, mergeMap, takeUntil, tap, toArray } from "rxjs/operators";
import { AdminService, BaseComponent, CommunitiesService, Community, EventBusService, UserService } from "@mypxplat/xplat/core";
import { AppService, WebCommunityService } from "@mypxplat/xplat/web/core";

/** How long after the last drop the order is written, so a run of drags saves once. */
const ORDER_SAVE_DELAY_MS = 1200;
/** Order writes in flight at once. */
const ORDER_SAVE_CONCURRENCY = 4;

/**
 * Admin surface for community CRUD. Mounted inside web-admin's `studiooneplus-page` as
 * the "Communities" section (admin-gated). Renders the list only — Add and row-click
 * navigate to the full-screen `/studiooneplus/communities/detail/:communityStringId` page
 * (or `.../new` for create). DENG-728 Phase 4.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-communities",
  templateUrl: "communities.component.html",
  standalone: false,
})
export class CommunitiesComponent extends BaseComponent {
  public communities: Community[];
  /** Each row's sub-lines, built once per load so the template binds a plain property. */
  public subDataById: Record<string, string[]> = {};
  /** Each row's icon (icon, else hero), built with the sub-lines. */
  public photoById: Record<string, string | null> = {};
  public loading: boolean;
  /** From a drop until the order is saved: drops are batched, then written in one pass. */
  public savingOrder = false;
  /** Each community's `order` as last read or written — what a save diffs against. */
  private _savedOrder: Record<string, number | undefined> = {};
  private _orderChanged$ = new Subject<void>();
  private _writingOrder = false;
  private _writeAgain = false;

  constructor(
    userService: UserService,
    public appService: AppService,
    public eventBusService: EventBusService,
    public communityService: WebCommunityService,
    public adminService: AdminService,
    public communitiesService: CommunitiesService,
    public router: Router,
    private _dragulaService: DragulaService
  ) {
    super(userService);
  }

  ngOnInit() {
    // Cached path — CommunitiesService memoizes on `communitiesMap`, so back-nav from a
    // detail view is instant. `<myp-community-detail>`'s save invalidates the cache
    // before nav; nothing else here mutates.
    this.load();
    // Drag to reorder: the list's order is the order members see communities in — Explore,
    // the hub's rail, the mobile app. Every community shares one sequence, hidden ones and
    // other environments' included, since they all live in the one Firestore collection.
    this._dragulaService
      .dropModel("COMMUNITIES")
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ targetModel }) => {
        this.communities = targetModel;
        this.savingOrder = true;
        this._orderChanged$.next();
      });
    // Rearranging the whole list is many drops in a row; write once they stop.
    this._orderChanged$.pipe(debounceTime(ORDER_SAVE_DELAY_MS), takeUntil(this.destroy$)).subscribe(() => this.saveOrder());
    // Prime the MySQL groups cache so opening a community's detail view is instant —
    // AdminService.getGroups() memoizes the first result on `cachedGroups`. Fire-and-forget;
    // the subsequent `.subscribe()` in <myp-community-detail> reuses the same cached list.
    this.adminService.getGroups().subscribe({
      error: (err) => console.warn("[admin/communities] pre-fetch getGroups failed", err),
    });
  }

  private load() {
    // Admin surface uses the include-hidden variant so managers can find and edit
    // communities the public /communities landing filters out. Bypasses the shared
    // `communities$` cache on purpose — otherwise a hidden community loaded here would
    // leak into the public grid the next time it read the cache.
    this.loading = true;
    this.communitiesService.loadAllForAdmin().then((result) => {
      this.loading = false;
      this.communities = [...(result || [])].sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
      this.subDataById = {};
      this.photoById = {};
      this._savedOrder = {};
      this.communities.forEach((c) => {
        this._savedOrder[c.string_id] = c.order;
        this.photoById[c.string_id] = c.branding?.icon_url || c.branding?.hero_image_url || null;
        // Which environments serve the community (paeapi `environments`; absent = all).
        const where = c.environments?.length ? "Only in: " + c.environments.join(", ") : "All environments";
        this.subDataById[c.string_id] = [c.string_id, "Post condition: " + c.participation_condition, where];
      });
    });
  }

  ngOnDestroy() {
    // Left the page inside the batching delay: write what's there rather than drop it.
    if (this.savingOrder && !this._writingOrder) this.saveOrder();
    super.ngOnDestroy();
  }

  /**
   * Persist the list's order as `order` 1..n. Only communities whose number differs from what
   * was last saved are written, a few at a time. One pass runs at a time: a drop landing
   * mid-pass waits for it, then the next pass writes whatever is still different — so a
   * slow early write can never overwrite a later one.
   */
  saveOrder() {
    if (this._writingOrder) {
      this._writeAgain = true;
      return;
    }
    const changed = this.communities.map((c, i) => ({ c, order: i + 1 })).filter(({ c, order }) => this._savedOrder[c.string_id] !== order);
    if (!changed.length) {
      this.savingOrder = false;
      return;
    }
    this._writingOrder = true;
    from(changed)
      .pipe(
        mergeMap(({ c, order }) => this.adminService.updateCommunity(c.string_id, { order }).pipe(tap(() => (this._savedOrder[c.string_id] = c.order = order))), ORDER_SAVE_CONCURRENCY),
        toArray()
      )
      .subscribe({
        next: () => {
          this._writingOrder = false;
          if (this._writeAgain) {
            this._writeAgain = false;
            this.saveOrder();
            return;
          }
          this.savingOrder = false;
          this.communitiesService.refresh();
        },
        error: (err) => {
          console.error("[admin/communities] saving order failed", err);
          this._writingOrder = this._writeAgain = this.savingOrder = false;
          this.appService.alert("Couldn't save the new order. The list has been reloaded.");
          this.load();
        },
      });
  }

  openDetail(community?: Community, event?: Event) {
    if (event) event.stopPropagation();
    const segment = community?.string_id ?? "new";
    this.router.navigate(["/studiooneplus", "communities", "detail", segment]);
  }
}
